import * as ngCommon from '@angular/common'
import * as ngCore from '@angular/core'
import { isDeepEqual } from '@attain-apps/data-utils'
import * as ents from '@attain-apps/entities'
import * as ds from '@attain-apps/fe/web/main/lib/design'
import { capitalize } from '@attain-apps/string-utils'
import * as rxjs from 'rxjs'

import * as state from '../../../../../../../../../state'
import * as rendering from '../../../../../rendering'

/**
 * Number of milliseconds to debounce rapid updates to the widget query
 *
 * I.e. if multiple updates are made in less than this number of milliseconds,
 * only the last of those updates will actually be applied
 */
const UPDATE_DEBOUNCE_MS = 500

@ngCore.Component({
  imports: [
    ngCommon.JsonPipe,
    ds.input.text.Component,
    ds.input.code.Component,
    ds.tabs.floatingHorizontal.Component,
    ds.tabs.tab.Directive,
    rendering.widget.Component,
  ],
  selector: 'atn-mosaic-dev-widget-general-tab',
  standalone: true,
  styleUrls: ['./component.scss'],
  templateUrl: './component.html',
})
export class Component {
  // NOTE: defined here just because we cant use namespace directly in the
  // template
  public readonly widgetRefIdForPreview =
    state.mosaic.models.widget.Ref.Id.FOR_PREVIEW
  /** @see {@link AtnMosaicEcService.widgetsCollection} */
  public readonly widgetsEc = ngCore.inject(state.mosaic.dev.widgets.Collection)

  /** @see {@link state.mosaic.dev.widgets.plugins.preview.Plugin} */
  public readonly widgetPreviewPlugin = this.widgetsEc.plugins
    .lookup(state.mosaic.dev.widgets.plugins.preview.Plugin)
    .get()

  /**
   * TODO: is this still needed?
   *
   * Now this is only used to utilize {@link rxjs.distinctUntilChanged}.
   * However, hopefully, the underlying edit action has its own logic for
   * filtering out things that haven't actually changed
   *
   * __OLD:__ Used to debounce rapid updates to avoid excessive action
   * dispatches -- This debouncing was causing bugs
   */
  private readonly _updatedQuery$ = new rxjs.BehaviorSubject<
    ents.core.mosaic.Widget.Query | undefined
  >(undefined)

  constructor() {
    /** @see {@link _updatedQuery$} */
    this._updatedQuery$
      .pipe(
        rxjs.debounceTime(UPDATE_DEBOUNCE_MS),
        rxjs.distinctUntilChanged(isDeepEqual),
        rxjs.map(query => {
          if (!query) {
            return
          }
          this.updateWidget('query', query)
        }),
      )
      .subscribe()
  }

  /**
   * Update widget property handler
   */
  public updateWidget<K extends keyof ents.core.mosaic.Widget.Input>(
    prop: K,
    value: ents.core.mosaic.Widget.Input[K],
  ): void {
    this.widgetsEc.plugins.editingPlugin.feature.actions.edit
      .from(`Dialog.${capitalize(prop)}.Inputted`)
      .dispatch({
        edit: widget => {
          if (!widget) {
            return
          }

          widget[prop] = value
        },
      })
  }

  /** TODO: */
  public updateWidgetQuery(query: unknown): void {
    // NOTE: we just hope this is structured properly
    this._updatedQuery$.next(query as ents.core.mosaic.Widget.Query)
  }

  /** TODO: */
  public runQuery = (source: string) => {
    const id = this.widgetsEc.plugins.currentEntityPlugin.feature.selectors
      .getId()
      .asSignal()
    const widget = this.widgetsEc.plugins.currentEntityPlugin.feature.selectors
      .getEditedEntity()
      .asSignal()
    if (id && widget?.query) {
      this.widgetPreviewPlugin.feature.actions.preview
        .from(`Dialog.${source}`)
        .dispatch({ flexibleIdentifier: { id }, query: widget.query })
    }
  }
}
